import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, Checkbox, Empty, Space, Spin, Tabs, Tag, Typography } from "antd";
import {
  agentPlatformApi,
  AgentPlatformApiError,
  OwnerAgent,
  OwnerItem,
  OwnerPlatform,
  OwnerResource,
  ResourceBinding,
} from "./api";
import "./AgentPlatformDashboard.css";
import Briefs from "./Briefs";
import GoogleConnections from "./GoogleConnections";
import Issues from "./Issues";
import Monitoring from "./Monitoring";
import SupportCases from "./SupportCases";
import { formatAgentDateTime } from "./dateTime";

const { Text, Title } = Typography;
const GOOGLE_READ_CAPABILITIES = new Set([
  "gmail.read", "gmail.thread.read", "calendar.read", "calendar.availability", "drive.read", "drive.content.read",
]);
const CAPABILITY_LABELS: Record<string, string> = {
  "gmail.read": "Read Gmail messages",
  "gmail.thread.read": "Read full Gmail thread context",
  "calendar.read": "Read calendars and events",
  "calendar.availability": "Read calendar availability",
  "drive.read": "Search Drive metadata",
  "drive.content.read": "Read selected Drive file content",
};
const defaultCapabilities = (available: string[]) => available.filter((capability) =>
  ["gmail.read", "calendar.read", "calendar.availability", "drive.read"].includes(capability));
const attentionStates = new Set(["needs_attention", "waiting_on_you"]);
const agentLabel: Record<string, string> = {
  chief_of_staff: "Chief of Staff",
  support: "Support",
  engineering: "Engineering",
};
const isCancelled = (error: unknown) => (error as { code?: string })?.code === "ERR_CANCELED";
const errorCopy = (error: unknown) =>
  error instanceof AgentPlatformApiError && error.status === 0
    ? "The Agent Platform service is unavailable. Start the service, then retry."
    : error instanceof AgentPlatformApiError && error.status === 403
    ? "You do not have access to the private Agent Platform."
    : error instanceof Error ? error.message : "Unable to load Agent Platform.";
const isAttention = (item: OwnerItem) => attentionStates.has(item.attentionState || "");
const safeSourceUrl = (value?: string) => {
  if (!value) return undefined;
  try {
    const url = new URL(value);
    return url.protocol === "https:" && !url.username && !url.password ? url.toString() : undefined;
  } catch (_error) { return undefined; }
};

function ItemCard({ item, onOpen }: { item: OwnerItem; onOpen?: () => void }) {
  return <Card size="small" title={<Space wrap><Tag>{agentLabel[item.agent] || item.agent}</Tag><Text strong>{item.title}</Text></Space>}
    extra={onOpen ? <Button type="link" onClick={onOpen}>Open</Button> : undefined}>
    <Space direction="vertical" size="small" className="agent-platform-full">
      <Space wrap><Tag color={isAttention(item) ? "gold" : "blue"}>{item.attentionState || item.status}</Tag><Text type="secondary">{item.status}</Text></Space>
      {item.detail?.summary && <Text>{item.detail.summary}</Text>}
      {item.detail?.findings?.length ? <ul>{item.detail.findings.map((finding, index) => <li key={`${item.id}-finding-${index}`}>{finding.text}</li>)}</ul> : null}
      {item.detail?.recommendation && <Text><strong>Recommendation:</strong> {item.detail.recommendation}</Text>}
      {item.detail?.affectedFiles?.length ? <ul>{item.detail.affectedFiles.map((file) => <li key={file.path}><strong>{file.path}</strong>: {file.reason}</li>)}</ul> : null}
      {item.detail?.fixCaseDraft && <Text><strong>Fix-case draft ready for review.</strong> {item.detail.fixCaseDraft.limitation}</Text>}
      {item.detail?.proposedChange && <Text><strong>Proposed change:</strong> {item.detail.proposedChange}</Text>}
      {item.detail?.regressionTests?.length ? <Text><strong>Regression tests:</strong> {item.detail.regressionTests.join(", ")}</Text> : null}
      {item.kind === "engineering_task" && item.detail?.codeDefectStatus &&
        <Text><strong>Code defect status:</strong> {item.detail.codeDefectStatus.replace(/_/g, " ")}
          {item.detail.codeDefectStatus === "not_found" ? " (inspected scope only)" : ""}</Text>}
      {item.kind === "engineering_task" && item.detail?.supportFindings?.length ? <div>
        <Text strong>Support findings</Text>
        <ul>{item.detail.supportFindings.map((finding, index) => <li key={`support-${index}`}>{finding.text}</li>)}</ul>
      </div> : null}
      {item.kind === "engineering_task" && item.detail?.reproductionRequests?.length ? <div>
        <Text strong>Proposed DEV reproductions</Text>
        <ul>{item.detail.reproductionRequests.map((request, index) => <li key={`reproduction-${index}`}>
          <Text>{request.reason}: book {request.bookId}, chapter {request.chapterId},
            {request.sourceLang} → {request.targetLangs.join(", ")};
            {request.repeats} repeat(s), paragraph index {request.paragraphIndex ?? 0}.</Text>
        </li>)}</ul>
        <Text type="secondary">Proposals are unexecuted; review and approval are available only in the local Support case.</Text>
      </div> : null}
      {item.kind === "engineering_task" && item.detail?.observedTests?.length ? <div>
        <Text strong>Observed tests</Text>
        <ul>{item.detail.observedTests.map((test, index) => <li key={`observed-${index}`}>
          <Text>{test.status.replace(/_/g, " ")}: {test.summary}</Text>
        </li>)}</ul>
      </div> : null}
      {item.kind === "engineering_task" && item.detail?.refundRecommendation && <Text>
        <strong>Refund recommendation:</strong> {item.detail.refundRecommendation.decision.replace(/_/g, " ")} — {item.detail.refundRecommendation.reason}
      </Text>}
      {item.kind === "engineering_task" && item.detail?.reproductionStatus && <Text>
        <strong>DEV reproduction:</strong> {item.detail.reproductionStatus.replace(/_/g, " ")}
        {item.detail.reproductionId ? ` (${item.detail.reproductionId})` : ""}
      </Text>}
      {item.detail?.draftText && <div><Text strong>Internal draft</Text><div className="agent-platform-proposal"><Text>{item.detail.draftText}</Text></div></div>}
      {item.sourceRefs.length ? <ul>{item.sourceRefs.map((source, index) => {
        const label = source.title || source.path || source.id || `Source ${index + 1}`;
        const url = safeSourceUrl(source.url);
        return <li key={`${source.id || source.path || "source"}-${index}`}>{url
          ? <a href={url} target="_blank" rel="noreferrer">{label}</a> : <span>{label}</span>}</li>;
      })}</ul> : null}
      <Text type="secondary">Updated {formatAgentDateTime(item.updatedAt) || "time unavailable"}</Text>
    </Space>
  </Card>;
}

function ChiefResourceSelector({ agent, resources, onSaved }: {
  agent: OwnerAgent;
  resources: OwnerResource[];
  onSaved: (agent: OwnerAgent) => void;
}) {
  const google = resources.filter((resource) => resource.enabled && resource.type === "google_account");
  const initial = Object.fromEntries(agent.resourceBindings
    .filter((binding) => google.some((resource) => resource.id === binding.resourceId))
    .map((binding) => [binding.resourceId, binding.capabilities]));
  const [selected, setSelected] = useState<Record<string, string[]>>(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  useEffect(() => setSelected(initial), [agent.configurationRevision]); // eslint-disable-line react-hooks/exhaustive-deps
  const save = async () => {
    setSaving(true); setError(undefined);
    const resourceBindings: ResourceBinding[] = Object.entries(selected).map(([resourceId, capabilities]) => ({
      resourceId, capabilities: capabilities.filter((capability) => GOOGLE_READ_CAPABILITIES.has(capability)),
    }));
    try {
      const response = await agentPlatformApi.updateChiefOfStaffResources(agent.configurationRevision, resourceBindings);
      onSaved(response.agent);
    } catch (saveError) {
      setError(saveError instanceof AgentPlatformApiError && saveError.status === 409
        ? "The resource configuration changed. Refresh before saving again."
        : errorCopy(saveError));
    } finally { setSaving(false); }
  };
  return <Space direction="vertical" className="agent-platform-full">
    <Text>Select up to four Google accounts this agent may read.</Text>
    {google.length ? <Space direction="vertical">{google.map((resource) => {
      const enabled = Object.prototype.hasOwnProperty.call(selected, resource.id);
      const capabilities = selected[resource.id] || [];
      return <Card size="small" key={resource.id} title={<Checkbox checked={enabled}
        disabled={!enabled && Object.keys(selected).length >= 4}
        onChange={(event) => setSelected((current) => {
          if (!event.target.checked) { const next = { ...current }; delete next[resource.id]; return next; }
          return { ...current, [resource.id]: defaultCapabilities(resource.capabilities) };
        })}>{resource.displayName}</Checkbox>}>
        <Checkbox.Group disabled={!enabled} value={capabilities}
          onChange={(values) => setSelected((current) => ({ ...current, [resource.id]: values.map(String) }))}>
          <Space direction="vertical">{resource.capabilities.filter((capability) => GOOGLE_READ_CAPABILITIES.has(capability)).map((capability) =>
            <Checkbox key={capability} value={capability}>{CAPABILITY_LABELS[capability] || capability}</Checkbox>)}</Space>
        </Checkbox.Group>
      </Card>;
    })}</Space> : <Empty description="No enabled Google resources." />}
    {error && <Alert type="error" showIcon message={error} />}
    <Button type="primary" loading={saving} disabled={saving || Object.keys(selected).length > 4 || Object.values(selected).some((capabilities) => capabilities.length === 0)} onClick={() => void save()}>Save account access</Button>
  </Space>;
}

export default function AgentPlatformDashboard(): React.ReactElement {
  const [platform, setPlatform] = useState<OwnerPlatform>();
  const [activeSection, setActiveSection] = useState("attention");
  const [focusTarget, setFocusTarget] = useState<OwnerItem>();
  const [focusVersion, setFocusVersion] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const generation = useRef(0);
  const controller = useRef<AbortController>();
  const load = useCallback(async (background = false) => {
    const current = ++generation.current;
    controller.current?.abort(); const next = new AbortController(); controller.current = next;
    if (!background) setLoading(true);
    setError(undefined);
    try {
      const response = await agentPlatformApi.owner(next.signal);
      if (current === generation.current) setPlatform((existing) => {
        if (!existing) return response;
        const localRevisions = new Map(existing.agents.map((agent) => [agent.id, agent]));
        return { ...response, agents: response.agents.map((agent) => {
          const local = localRevisions.get(agent.id);
          return local && local.configurationRevision > agent.configurationRevision ? local : agent;
        }) };
      });
    } catch (loadError) {
      if (current === generation.current && !isCancelled(loadError)) {
        if (!background) setPlatform(undefined);
        setError(errorCopy(loadError));
      }
    } finally { if (current === generation.current && !background) setLoading(false); }
  }, []);
  useEffect(() => {
    void load();
    const poll = window.setInterval(() => void load(true), 30_000);
    return () => { window.clearInterval(poll); generation.current += 1; controller.current?.abort(); };
  }, [load]);

  const agents = platform?.agents || [];
  const chief = agents.find((agent) => agent.id === "chief_of_staff");
  const support = agents.find((agent) => agent.id === "support");
  const engineering = agents.find((agent) => agent.id === "engineering");
  const attention = useMemo(() => (platform?.items || []).filter(isAttention), [platform]);
  const engineeringItems = (platform?.items || []).filter((item) => item.kind === "engineering_task");
  const openItem = (item: OwnerItem) => {
    setFocusTarget(item); setFocusVersion((value) => value + 1);
    setActiveSection(item.agent === "support" ? "support" : item.agent === "engineering" ? "engineering" : "agents");
  };
  useEffect(() => {
    if (!focusTarget) return undefined;
    const prefix = focusTarget.kind === "task" ? "agent-platform-issue-" : focusTarget.kind === "support_case" ? "agent-platform-support-" : "";
    if (!prefix) return undefined;
    const timer = window.setTimeout(() => {
      const target = document.getElementById(`${prefix}${focusTarget.id}`);
      target?.scrollIntoView?.({ block: "nearest" });
      target?.focus?.({ preventScroll: true });
    }, 0);
    return () => window.clearTimeout(timer);
  }, [activeSection, focusTarget, focusVersion]);
  const updateAgent = (updated: OwnerAgent) => setPlatform((current) => current ? ({ ...current, agents: current.agents.map((agent) => agent.id === updated.id ? updated : agent) }) : current);
  const resourceWorkspaces = Array.from(new Set((platform?.resources || []).filter((resource) => resource.type === "google_account").map((resource) => resource.workspaceId)));

  if (loading && !platform) return <main className="agent-platform"><div className="agent-platform-loading"><Spin tip="Loading Agent Platform…" /></div></main>;
  if (!platform) return <main className="agent-platform" aria-labelledby="agent-platform-title"><Title id="agent-platform-title" level={2}>Agent Platform</Title>
    <Alert type="error" showIcon message={error || "Unable to load Agent Platform."} action={<Button size="small" onClick={() => void load()}>Retry</Button>} /></main>;

  return <main className="agent-platform" aria-labelledby="agent-platform-title">
    <header><Title id="agent-platform-title" level={2}>Agent Platform</Title><Space><Text type="secondary">Your private agents, resources, and durable work.</Text><Button onClick={() => void load()} loading={loading}>Refresh</Button></Space></header>
    {error && <Alert type="error" showIcon message={error} />}
    <Tabs activeKey={activeSection} onChange={setActiveSection} destroyOnHidden={false} items={[
      { key: "attention", label: <Badge size="small" count={attention.length} offset={[6, 0]}>Needs Attention</Badge>, children: attention.length
        ? <div className="agent-platform-grid">{attention.map((item) => <ItemCard key={`${item.kind}:${item.id}`} item={item}
          onOpen={platform.readOnly && item.kind === "task" ? undefined : () => openItem(item)} />)}</div>
        : <Empty description="Nothing needs your attention." /> },
      { key: "agents", label: "Agents", children: <div className="agent-platform-section-stack">
        <div className="agent-platform-grid">{agents.map((agent) => <Card key={agent.id} title={agent.name || agentLabel[agent.id]} extra={<Tag>{(platform.items || []).filter((item) => item.agent === agent.id && isAttention(item)).length} need attention</Tag>}>
          <Text type="secondary">{agent.id === "chief_of_staff"
            ? `${agent.resourceBindings.length} selected resource${agent.resourceBindings.length === 1 ? "" : "s"}`
            : "Access is scoped to each authorized case."}</Text>
          {agent.id === "chief_of_staff" && <ChiefResourceSelector agent={agent} resources={platform.resources} onSaved={updateAgent} />}
        </Card>)}</div>
        {!platform.readOnly && chief?.storageWorkspaceIds.map((workspaceId) => <section key={workspaceId} className="agent-platform-section-stack"><Issues workspaceId={workspaceId}
          focusIssueId={focusTarget?.kind === "task" && focusTarget.workspaceId === workspaceId ? focusTarget.id : undefined} /></section>)}
        {!platform.readOnly && chief?.storageWorkspaceIds[0] && <Monitoring workspaceId={chief.storageWorkspaceIds[0]} />}
      </div> },
      { key: "support", label: "Support", children: support?.storageWorkspaceIds[0]
        ? platform.readOnly
          ? <div className="agent-platform-grid">{platform.items.filter((item) => item.kind === "support_case").map((item) => <ItemCard key={item.id} item={item} />)}</div>
          : <SupportCases workspaceId={support.storageWorkspaceIds[0]} onActivityChanged={() => void load()}
          focusCaseId={focusTarget?.kind === "support_case" ? focusTarget.id : undefined} focusToken={focusVersion} />
        : <Empty description="Support is not configured." /> },
      { key: "engineering", label: "Engineering", children: engineeringItems.length
        ? <div className="agent-platform-grid">{engineeringItems.map((item) => <ItemCard key={item.id} item={item} />)}</div>
        : <Empty description={engineering ? "No engineering tasks." : "Engineering is not configured."} /> },
      { key: "connections", label: "Connections", children: <div className="agent-platform-section-stack">
        {(platform.resources || []).length ? <div className="agent-platform-grid">{platform.resources.map((resource) => <Card key={resource.id} size="small" title={resource.displayName}><Space wrap><Tag>{resource.type}</Tag><Tag color={resource.enabled ? "green" : "default"}>{resource.enabled ? "Enabled" : "Disabled"}</Tag></Space></Card>)}</div> : null}
        {resourceWorkspaces.map((workspaceId) => <GoogleConnections key={workspaceId} workspaceId={workspaceId} />)}
      </div> },
      { key: "history", label: "History", children: <div className="agent-platform-section-stack">
        {(platform.activity || []).length ? <div className="agent-platform-grid">{platform.activity.map((entry) => <Card size="small" key={`${entry.kind}:${entry.id}`} title={entry.title}><Space direction="vertical"><Space><Tag>{entry.kind}</Tag><Tag>{agentLabel[entry.agent] || entry.agent}</Tag><Tag>{entry.status}</Tag></Space><Text type="secondary">{formatAgentDateTime(entry.updatedAt) || "Time unavailable"}</Text></Space></Card>)}</div> : <Empty description="No activity yet." />}
        {!platform.readOnly && chief?.storageWorkspaceIds.map((workspaceId) => <Briefs key={workspaceId} workspaceId={workspaceId} />)}
      </div> },
    ]} />
  </main>;
}
